import { memo, useCallback } from 'react';
import FavouriteButton from 'src/components/Buttons/FavouriteButton';
import SortIcon from 'src/components/Icons/SortIcon';
import {
	MarketSelectorTableHeaderConfig,
	MarketSelectorTableHeaderValue,
} from 'src/components/MarketSelector/MarketSelectorTable/types';
import useGetCellDisplayStatus from 'src/components/MarketSelector/MarketSelectorTable/hooks/useGetCellDisplayStatus';
import { MarketCategory } from 'src/constants/markets';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cm } from 'src/utils/classNames';

interface Props {
	header: MarketSelectorTableHeaderConfig;
	marketCategory: MarketCategory;
	currentMarketIsPerp: boolean;
	toggleSort: (header: MarketSelectorTableHeaderValue | undefined) => void;
	sort?: { header: MarketSelectorTableHeaderValue; isDescending: boolean };
	fundingRateUnit: string;
	toggleFundingRateUnit: () => void;
	isFavoritesSticky: boolean;
	toggleIsFavoritesSticky: () => void;
}

function MarketSelectorTableHeader({
	header,
	marketCategory,
	currentMarketIsPerp,
	toggleSort,
	sort,
	fundingRateUnit,
	toggleFundingRateUnit,
	isFavoritesSticky,
	toggleIsFavoritesSticky,
}: Props) {
	const getDisplayStatus = useGetCellDisplayStatus(
		marketCategory === 'spot' ? false : currentMarketIsPerp,
		currentMarketIsPerp
	);
	const isMobileScreenSize = useIsMobileScreenSize();
	const breakpoint = isMobileScreenSize ? 'mobile-only' : 'desktop-only';

	const baseDisplayStatus = getDisplayStatus(header.value);
	const shouldDisplay =
		baseDisplayStatus && ['all', breakpoint].includes(header.breakpoint);

	const handleSort = useCallback(() => {
		toggleSort(header.value);
	}, [toggleSort, header.value]);

	if (!shouldDisplay) {
		return null;
	}

	const sortState = (() => {
		if (sort?.header !== header.value) {
			return 'neutral';
		}

		return sort.isDescending ? 'descending' : 'ascending';
	})();

	return (
		<div
			className={cm(
				'flex cursor-pointer items-center justify-end gap-2xs whitespace-nowrap select-none',
				'typo-body-2xs text-text-secondary md:typo-body-xs',
				header.value === 'market' ? 'justify-start text-left' : 'text-right'
			)}
		>
			{header.value === 'market' && (
				<FavouriteButton
					highlighted={isFavoritesSticky}
					onClick={toggleIsFavoritesSticky}
					wrapperClassName="p-2 -m-2 mr-0"
					starSizePx={12}
				/>
			)}

			{header.value === 'funding' && (
				<span className="mr-2xs" onClick={toggleFundingRateUnit}>
					({fundingRateUnit === 'h' ? '1h' : 'APR'})
				</span>
			)}
			<span onClick={handleSort}>{header.label}</span>
			<SortIcon onClick={handleSort} state={sortState} size={16} />
		</div>
	);
}

export default memo(MarketSelectorTableHeader);
